// @ts-nocheck
import { Box } from '@mui/material';
import { Droppable } from 'react-beautiful-dnd';
import DraggableList from '../../../Draggable/DraggableList/DraggableList';
import { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import AmenityRow from '../AmenityRow/AmenityRow';
import './AmenitiesTable.css';
import AddAmenityRow from '../AddAmenityRow';
import { useSelector } from 'react-redux';
import { REORGANIZE_AMENITIES } from '../../../../constants/dragAndDrop';
import NotAllowedToDrop from '../../../Trip/Library/NotAllowedToDrop/NotAllowedToDrop';

/**
 * Functional React component for rendering an amenities table that supports drag and drop.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {Array} props.amenitiesList - The amenities list to display
 * @param {function} props.handleUpdateSequence - The function to handle update sequence for internal reordering
 * @param {function} props.updateAmenity - The function to handle update amenity
 * @param {function} props.handleDelete - The function to handle delete amenity
 * @param {boolean} props.isDropDisabled - The flag to disable drop
 * @param {boolean} props.isAddAmenityFormVisible - The flag to show add amenity form
 * @param {function} props.setAmenitiesList - The function to set amenities list
 * @param {function} props.toggleVisibility - The function to toggle visibility
 * @param {string|number} props.tripid - The trip id
 * @param {string} props.droppableId - Droppable ID for drag and drop integration
 *
 * @returns {JSX.Element} React element representing the amenities table.
 */
const AmenitiesTable = ({
  amenitiesList,
  handleUpdateSequence,
  updateAmenity,
  handleDelete,
  isDropDisabled = false,
  isAddAmenityFormVisible,
  setAmenitiesList,
  toggleVisibility,
  tripid,
  droppableId
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [amenityRows, setAmenityRows] = useState([]);
  const { t } = useTranslation(['settings', 'common', 'trip']);
  const showDefaultColumn = tripid === undefined;
  const [isEditing, setIsEditing] = useState(false);
  const lastActionRef = useRef(null);

  const isDragging = useSelector((state) => state.library?.isDragging ?? false);
  const isAmenitiesTableDraggableDisabled = useSelector(
    (state) =>
      state.library?.itinerary?.isAmenitiesTableDraggableDisabled ?? false
  );
  const draggableAction = useSelector(
    (state) => state.library?.itinerary?.draggableAction ?? null
  );

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    const {
      type = null,
      sourceIndex = 0,
      destinationIndex = 0
    } = draggableAction ?? {};

    // Create a unique action ID to avoid processing the same action multiple times
    const actionId = `${type}_${sourceIndex}_${destinationIndex}`;

    // Skip if we've already processed this exact action or if type is not relevant
    if (
      type !== REORGANIZE_AMENITIES ||
      lastActionRef.current === actionId ||
      amenityRows.length === 0 ||
      sourceIndex >= amenityRows.length ||
      destinationIndex >= amenityRows.length
    ) {
      return;
    }

    // Store the current action ID to prevent reprocessing
    lastActionRef.current = actionId;

    const sourceAmenity = amenityRows[sourceIndex];
    const destinationAmenity = amenityRows[destinationIndex];

    if (sourceAmenity && destinationAmenity) {
      handleUpdateSequence(sourceAmenity, destinationAmenity);
    }
  }, [draggableAction, amenityRows, handleUpdateSequence]);

  useEffect(() => {
    const setupAmenities = () => {
      if (amenitiesList && amenitiesList.length > 0) {
        const amenityRowsList = [];
        amenitiesList.forEach((amenity, index) => {
          const newAmenity = {
            id: amenity?.tripamenityid || amenity?.amenityid,
            amenityid: amenity?.amenityid,
            tripamenityid: amenity?.tripamenityid,
            sequence: amenity?.seq || index + 1,
            content: (
              <AmenityRow
                key={`amenity-row-${amenity?.tripamenityid || amenity?.amenityid}`}
                amenity={amenity}
                updateAmenity={updateAmenity}
                handleDelete={handleDelete}
                setAmenitiesList={setAmenitiesList}
                hideDefaultColumn={!showDefaultColumn}
                tripid={tripid}
                handleEdit={(value) => {
                  setIsEditing(value);
                }}
              />
            )
          };
          amenityRowsList.push(newAmenity);
        });
        setAmenityRows(amenityRowsList);
      } else {
        setAmenityRows([]);
      }
    };

    setupAmenities();
  }, [
    amenitiesList,
    handleDelete,
    setAmenitiesList,
    showDefaultColumn,
    tripid,
    updateAmenity
  ]);

  return (
    <Box
      className={`amenities-table-container ${
        !showDefaultColumn ? 'hide-default-column' : ''
      }`}
    >
      <table className='amenities-table-columns'>
        <thead>
          <tr>
            <th>{showDefaultColumn ? t('settings:default') : ''}</th>
            <th id='add-amenity-name-column'></th>
            <th>{t('settings:included')}</th>
            <th>{t('settings:excluded')}</th>
            <th id='add-amenity-actions-column'></th>
          </tr>
        </thead>
      </table>
      {isAddAmenityFormVisible && (
        <AddAmenityRow
          isVisible={isAddAmenityFormVisible}
          setAmenitiesList={setAmenitiesList}
          toggleVisibility={toggleVisibility}
          hideDefaultColumn={!showDefaultColumn}
          tripid={tripid}
        />
      )}

      {isMounted && (
        <Droppable
          droppableId={droppableId || 'amenities-table-droppable'}
          isDropDisabled={
            isDropDisabled || isEditing || isAmenitiesTableDraggableDisabled
          }
        >
          {(provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className={`amenities-droppable-container ${
                snapshot.isDraggingOver
                  ? 'amenities-droppable-container--dragging-over'
                  : ''
              }`}
            >
              {isAmenitiesTableDraggableDisabled && isDragging && (
                <NotAllowedToDrop id={'amenities-table-not-allowed-to-drop'} />
              )}
              {amenityRows.length > 0 && <DraggableList items={amenityRows} />}
              {provided.placeholder}
              {snapshot.isDraggingOver &&
                !isAmenitiesTableDraggableDisabled && (
                  <div className='amenity-drop-indicator'>
                    <span>{t('trip:dropToAddAmenity')}</span>
                  </div>
                )}
              {amenityRows.length === 0 && !snapshot.isDraggingOver && (
                <div className='no-amenities-message'>
                  <span>{t('settings:noAmenities')}</span>
                </div>
              )}
            </div>
          )}
        </Droppable>
      )}
    </Box>
  );
};

AmenitiesTable.propTypes = {
  amenitiesList: PropTypes.array,
  handleUpdateSequence: PropTypes.func,
  updateAmenity: PropTypes.func,
  handleDelete: PropTypes.func,
  isDropDisabled: PropTypes.bool,
  isAddAmenityFormVisible: PropTypes.bool,
  setAmenitiesList: PropTypes.func,
  toggleVisibility: PropTypes.func,
  tripid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  droppableId: PropTypes.string
};

export default AmenitiesTable;
